// @flow
import React, { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { find } from 'lodash'
import moment from 'moment'
import { OverlayTrigger, Tooltip, Glyphicon } from 'react-bootstrap'
import { WidthProvider, Responsive } from 'react-grid-layout'

import DescriptionTable from './DescriptionTable/DescriptionTable'
import TableWidget from './table-widget'
import DateRangeFilter from '../DateRangeFilter'
// import DataSummary from './niq-data-summary'  // NOTE will be enable in IN-1810
import constants from '../../constants/niq-constants'
import { DATA_IMPACT_SOURCE, NIQ_DATA_SOURCES, OMNISALES_SOURCE } from 'components/NIQSearchPage/SearchDictionaryFilter'
import { KEY, LAYOUTS, LAYOUT_VERSION_NUMBER, ROW_HEIGHT } from './niq-layouts'
import ResizableGridHelper from '../../../../utils/layout-helper'
import '../../../../styles/widget.scss'

const {
  aggregations: {
    BRAND,
    CATEGORY,
    MERCHANT,
    DESCRIPTION_FIRST_WORD,
    SLICE_CATEGORY,
    SLICE_BRAND,
    INVALID_CATEGORY,
    INVALID_BRAND,
    OMNISALES_MODULE
  }
} = constants

const ResponsiveReactGridLayout = WidthProvider(Responsive)
const layoutHelper = new ResizableGridHelper(KEY, LAYOUTS, LAYOUT_VERSION_NUMBER)

class WidgetWrapper extends Component {
  static propTypes = {
    isLayoutLocked: PropTypes.bool,
    handleDateSelection: PropTypes.func,
    rangeStart: PropTypes.string,
    rangeEnd: PropTypes.string,
    from: PropTypes.string,
    to: PropTypes.string,
    lastLoadDate: PropTypes.object,
    dataIndex: PropTypes.string,
    niqDataSource: PropTypes.string
  }

  constructor(props) {
    super(props)
    const layouts = layoutHelper.getLayouts()
    this.state = {
      layout: layouts.lg,
      layouts,
      gridWidth: 1200,
      noOfColumns: 12
    }
  }

  onLayoutChange = (layout, layouts) => {
    layoutHelper.setLayouts(layouts)
    this.setState({ layout, layouts })
  }

  onWidthChange = (
    containerWidth: number,
    margin: [number, number],
    cols: number,
    containerPadding: [number, number]
  ) => {
    this.setState({ gridWidth: containerWidth, noOfColumns: cols })
  }

  /**
   * Fetches height of a widgets
   * @memberof WidgetWrapper
   */

  getWidgetHeight = key => {
    try {
      if (!this.state.layout) {
        return null
      }
      const widget = find(this.state.layout, obj => obj.i === key)
      return widget.h * ROW_HEIGHT
    } catch (err) {
      layoutHelper.setLayouts()
    }
  }

  /**
   * Fetches approx width of the widget.
   * May have minor offsets due to margin between widgets.
   * @memberof WidgetWrapper
   */

  getWidgetWidth = key => {
    try {
      if (!this.state.layout) {
        return null
      }
      const widget = this.state.layout.find(obj => obj.i === key)
      return (widget.w * this.state.gridWidth) / this.state.noOfColumns
    } catch (err) {
      layoutHelper.setLayouts()
    }
  }

  renderWidget = (key, aggType, title) => {
    return (
      <div key={key}>
        <TableWidget
          width={this.getWidgetWidth(key)}
          height={this.getWidgetHeight(key)}
          aggType={aggType}
          getTitle={count => `Top ${count || 0} ${title}`}
        />
      </div>
    )
  }

  getLocalTime = value => {
    return value
      ? moment
          .utc(value)
          .local()
          .format(constants.dateFormat.refreshData)
      : '--'
  }

  loadInfoTooltip = () => {
    const { lastLoadDate } = this.props

    const loadDate = lastLoadDate.searchNIQData

    return (
      <Tooltip id="refresh-load-info">
        {'Data Refresh: '}
        <span>{this.getLocalTime(loadDate)}</span>
      </Tooltip>
    )
  }

  selectSource = () => {
    switch (this.props.niqDataSource) {
      case OMNISALES_SOURCE:
        return NIQ_DATA_SOURCES[0].value
      case DATA_IMPACT_SOURCE:
        return NIQ_DATA_SOURCES[1].value
      default:
        return 'Source not selected'
    }
  }

  render() {
    const { rangeStart, rangeEnd, from, to, handleDateSelection, isLayoutLocked, lastLoadDate } = this.props
    return (
      <div className="widget-wrapper">
        <div className="widget-filters" style={{ position: 'absolute', display: 'flex', top: -38, right: 70 }}>
          {lastLoadDate && (
            <OverlayTrigger placement="left" overlay={this.loadInfoTooltip()}>
              <Glyphicon style={{ paddingTop: '10px' }} className="pull-right" glyph="info-sign" />
            </OverlayTrigger>
          )}
          <DateRangeFilter
            handleDateSelection={handleDateSelection}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            className="pull-right widget-filters-daterange"
            startDate={from}
            endDate={to}
          />
          <div className="niq-data-source">{this.selectSource()}</div>
        </div>
        <ResponsiveReactGridLayout
          onResizeStop={this.onResizeStop}
          onLayoutChange={this.onLayoutChange}
          onWidthChange={this.onWidthChange}
          layouts={this.state.layouts}
          cols={{ lg: 12, md: 9, sm: 6, xs: 4, xxs: 2 }}
          rowHeight={ROW_HEIGHT}
          width={1200}
          isDraggable={!isLayoutLocked}
          isResizable={!isLayoutLocked}
          className={isLayoutLocked ? 'locked' : 'unlocked'}
          margin={[15, 15]}
        >
          <div key="description-widget">
            <DescriptionTable height={this.getWidgetHeight('description-widget')} />
          </div>
          {this.renderWidget('category-widget', CATEGORY, 'Categories')}
          {this.renderWidget('brand-widget', BRAND, 'Brands')}
          {this.renderWidget('merchant-widget', MERCHANT, 'Merchants')}
          {this.renderWidget('firstword-widget', DESCRIPTION_FIRST_WORD, 'First Words')}

          {this.renderWidget('slice-category-widget', SLICE_CATEGORY, 'Slice Categories')}
          {this.renderWidget('slice-brand-widget', SLICE_BRAND, 'Slice Brand')}

          {this.renderWidget('invalid-category-widget', INVALID_CATEGORY, 'Invalid Categories')}
          {this.renderWidget('invalid-brand-widget', INVALID_BRAND, 'Invalid Brands')}
          {this.renderWidget('omnisales-module-widget', OMNISALES_MODULE, 'Omnisales Module')}
          {/* <div key="stats-widget">
            <DataSummary width={this.getWidgetWidth('stats-widget')} />
          </div> */}
        </ResponsiveReactGridLayout>
      </div>
    )
  }
}

function mapStateToProps(state) {
  return {
    niqDataSource: state.niqSearch.searchFilters.dataImpactFilter
  }
}

export default connect(mapStateToProps)(WidgetWrapper)
